function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function normalizeDigits(value) {
  return String(value || "").replace(/\D+/g, "");
}

function splitTags(value) {
  if (Array.isArray(value)) return value.map((item) => String(item || "").trim()).filter(Boolean);
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeRoles(input) {
  const base = input && typeof input === "object" ? input : {};
  return {
    owner: base.owner === true,
    supplier: base.supplier === true,
    provider: base.provider === true,
    customer: base.customer === true,
  };
}

function buildIdentityKey(base = {}) {
  const doc = normalizeDigits(base.documentDigits || base.document || base.cpfCnpj || base.cnpj || base.cpf || "");
  if (doc) return `doc:${doc}`;
  const name = normalizeText(base.name || base.razaoSocial || base.nome || "");
  const email = normalizeText(base.email || "");
  const phone = normalizeDigits(base.phone || base.whatsapp || base.contact || base.telefone || "").slice(-11);
  return `alt:${name}|${email}|${phone}`;
}

function uniqueList(values) {
  return Array.from(new Set((Array.isArray(values) ? values : []).map((item) => String(item || "").trim()).filter(Boolean)));
}

function toSupplierFromPerson(person) {
  const roles = normalizeRoles(person?.roles);
  if (!roles.supplier) return null;
  const details = person?.roleDetails?.supplier && typeof person.roleDetails.supplier === "object" ? person.roleDetails.supplier : {};
  const categories = splitTags(details.categories);
  return {
    id: String(person?.id || person?.clientId || "").trim(),
    name: String(person?.tradeName || person?.name || "").trim(),
    legalName: String(person?.name || "").trim(),
    email: String(person?.email || "").trim(),
    contact: String(person?.phone || person?.whatsapp || "").trim(),
    phone: String(person?.phone || "").trim(),
    whatsapp: String(person?.whatsapp || "").trim(),
    category: String(categories[0] || "").trim(),
    categories,
    active: person?.active !== false,
    documentDigits: normalizeDigits(person?.documentDigits || person?.document || ""),
    source: "people",
    sourcePersonId: String(person?.id || person?.clientId || "").trim(),
    raw: person,
  };
}

function toSupplierFromLegacy(supplier) {
  if (!supplier) return null;
  return {
    id: String(supplier?.id || supplier?.clientId || "").trim(),
    name: String(supplier?.name || supplier?.tradeName || "").trim(),
    legalName: String(supplier?.name || "").trim(),
    email: String(supplier?.email || "").trim(),
    contact: String(supplier?.contact || supplier?.phone || supplier?.telefone || "").trim(),
    phone: String(supplier?.phone || supplier?.telefone || supplier?.contact || "").trim(),
    whatsapp: String(supplier?.whatsapp || "").trim(),
    category: String(supplier?.category || "").trim(),
    categories: uniqueList([supplier?.category]),
    active: supplier?.active !== false,
    documentDigits: normalizeDigits(supplier?.documentDigits || supplier?.document || supplier?.cpfCnpj || supplier?.cnpj || supplier?.cpf || ""),
    source: "legacy",
    raw: supplier,
  };
}

function toProviderFromPerson(person) {
  const roles = normalizeRoles(person?.roles);
  if (!roles.provider) return null;
  const details = person?.roleDetails?.provider && typeof person.roleDetails.provider === "object" ? person.roleDetails.provider : {};
  const specialties = splitTags(details.specialties);
  const serviceCategories = splitTags(details.serviceCategories);
  return {
    id: String(person?.id || person?.clientId || "").trim(),
    name: String(person?.tradeName || person?.name || "").trim(),
    legalName: String(person?.name || "").trim(),
    email: String(person?.email || "").trim(),
    contact: String(person?.phone || person?.whatsapp || "").trim(),
    phone: String(person?.phone || "").trim(),
    whatsapp: String(person?.whatsapp || "").trim(),
    specialty: String(specialties[0] || serviceCategories[0] || "").trim(),
    specialties,
    serviceCategories,
    coverageArea: String(details.coverageArea || "").trim(),
    defaultCostCenter: String(details.defaultCostCenter || "").trim(),
    active: person?.active !== false,
    documentDigits: normalizeDigits(person?.documentDigits || person?.document || ""),
    source: "people",
    sourcePersonId: String(person?.id || person?.clientId || "").trim(),
    raw: person,
  };
}

function toProviderFromLegacy(provider) {
  if (!provider) return null;
  const specialty = String(provider?.specialty || provider?.specialities || provider?.category || "").trim();
  return {
    id: String(provider?.id || provider?.clientId || "").trim(),
    name: String(provider?.name || provider?.tradeName || "").trim(),
    legalName: String(provider?.name || "").trim(),
    email: String(provider?.email || "").trim(),
    contact: String(provider?.phone || provider?.contact || provider?.telefone || "").trim(),
    phone: String(provider?.phone || provider?.telefone || provider?.contact || "").trim(),
    whatsapp: String(provider?.whatsapp || "").trim(),
    specialty,
    specialties: uniqueList([specialty]),
    serviceCategories: uniqueList([provider?.category]),
    coverageArea: String(provider?.coverageArea || provider?.region || "").trim(),
    defaultCostCenter: String(provider?.defaultCostCenter || provider?.costCenter || "").trim(),
    active: provider?.active !== false,
    documentDigits: normalizeDigits(provider?.documentDigits || provider?.document || provider?.cpfCnpj || provider?.cnpj || provider?.cpf || ""),
    source: "legacy",
    raw: provider,
  };
}

function dedupeDirectory(items) {
  const map = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item || item.active === false) return;
    const idKey = String(item.id || "").trim();
    const identityKey = buildIdentityKey(item);
    const key = idKey || identityKey;
    if (!key) return;
    const current = map.get(key);
    if (!current) {
      map.set(key, item);
      return;
    }
    if (current.source === "legacy" && item.source === "people") {
      map.set(key, item);
      return;
    }
    const currentName = normalizeText(current.name || current.legalName || "");
    const nextName = normalizeText(item.name || item.legalName || "");
    if (!currentName && nextName) map.set(key, item);
  });
  return Array.from(map.values()).sort((a, b) => String(a.name || a.legalName || "").localeCompare(String(b.name || b.legalName || ""), "pt-BR"));
}

export function buildSupplierDirectory(people, suppliers) {
  return dedupeDirectory([
    ...(Array.isArray(people) ? people : []).map(toSupplierFromPerson).filter(Boolean),
    ...(Array.isArray(suppliers) ? suppliers : []).map(toSupplierFromLegacy).filter(Boolean),
  ]);
}

export function buildProviderDirectory(people, providers) {
  return dedupeDirectory([
    ...(Array.isArray(people) ? people : []).map(toProviderFromPerson).filter(Boolean),
    ...(Array.isArray(providers) ? providers : []).map(toProviderFromLegacy).filter(Boolean),
  ]);
}
